import React from 'react';
import { createRoot, Root } from 'react-dom/client';
import { App } from './App';
import type { BundleConfig } from './types';
import './styles.css';

type DiscountCalculatorApi = {
  init: (config?: BundleConfig) => void;
  mount: (
    container: HTMLElement | string,
    config?: BundleConfig
  ) => (() => void) | undefined;
  unmount: (container: HTMLElement | string) => void;
};

const mountedRoots = new Map<HTMLElement, Root>();

function resolveApiUrl(config?: BundleConfig): string {
  return (
    config?.apiUrl ||
    document.querySelector('[data-intan-api-url]')?.getAttribute('data-intan-api-url') ||
    (document.querySelector('script[data-intan-api-url]') as HTMLScriptElement)?.dataset
      .intanApiUrl ||
    ''
  );
}

function resolveContainer(target: HTMLElement | string): HTMLElement | null {
  if (typeof target === 'string') {
    return document.querySelector(target) as HTMLElement | null;
  }
  return target;
}

function mountInto(
  container: HTMLElement,
  apiUrl: string,
  liveSessionId?: string
): () => void {
  let root = mountedRoots.get(container);
  if (!root) {
    root = createRoot(container);
    mountedRoots.set(container, root);
  }
  root.render(React.createElement(App, { apiUrl, liveSessionId }));
  return () => {
    const current = mountedRoots.get(container);
    if (current) {
      current.unmount();
      mountedRoots.delete(container);
    }
  };
}

function createDefaultContainer(apiUrl: string): HTMLElement {
  const container = document.createElement('div');
  container.id = 'intan-discount-calculator';
  container.setAttribute('data-intan-discount-container', '');
  container.setAttribute('data-intan-api-url', apiUrl);
  container.style.cssText = `
    margin: 16px 0;
    padding: 0;
    width: 100%;
  `;
  return container;
}

function findInsertLocation(): { parent: HTMLElement; before?: Node } | null {
  const selectors = [
    '.cart-drawer__footer',
    '.cart__footer',
    '.cart-summary',
    '.cart-totals',
    '[data-cart-summary]',
    '.cart-items',
    '.cart-drawer__inner',
    '.cart__inner',
    '.cart-container',
    '.cart',
    '[data-cart]',
    'form[action*="cart"]',
  ];

  for (const selector of selectors) {
    const element = document.querySelector(selector) as HTMLElement;
    if (element) {
      const checkoutBtn = element.querySelector(
        'button[type="submit"][name="checkout"], button[name="checkout"], [data-checkout], .cart__checkout, .cart-drawer__checkout'
      );

      if (checkoutBtn && checkoutBtn.parentElement) {
        return { parent: checkoutBtn.parentElement, before: checkoutBtn };
      }

      return { parent: element, before: element.firstChild || undefined };
    }
  }

  return null;
}

const api: DiscountCalculatorApi = {
  /**
   * Mount the cart UI into an existing element (or selector).
   * Returns an unmount function. Prefer this for overlays (e.g. LIVE cart modal).
   */
  mount: (container, config = {}) => {
    const apiUrl = resolveApiUrl(config);
    if (!apiUrl) {
      console.error(
        'IntanDiscountCalculator: API URL is required. Provide it via config or data-intan-api-url attribute.'
      );
      return undefined;
    }
    const el = resolveContainer(container);
    if (!el) {
      console.error('IntanDiscountCalculator: mount container not found');
      return undefined;
    }
    el.setAttribute('data-intan-api-url', apiUrl);
    return mountInto(el, apiUrl.replace(/\/$/, ''), config.liveSessionId);
  },

  unmount: (container) => {
    const el = resolveContainer(container);
    if (!el) return;
    const root = mountedRoots.get(el);
    if (root) {
      root.unmount();
      mountedRoots.delete(el);
    }
  },

  init: (config = {}) => {
    const apiUrl = resolveApiUrl(config);
    if (!apiUrl) {
      console.error(
        'IntanDiscountCalculator: API URL is required. Provide it via config or data-intan-api-url attribute.'
      );
      return;
    }

    const initialize = () => {
      let container: HTMLElement | null = null;

      // Prefer explicit containerSelector when provided (LIVE modal, custom embeds)
      if (config.containerSelector) {
        container = document.querySelector(config.containerSelector) as HTMLElement | null;
      }

      if (!container) {
        container = document.getElementById('intan-discount-calculator') as HTMLElement | null;
      }

      if (!container) {
        container = document.querySelector(
          '[data-intan-discount-container]'
        ) as HTMLElement | null;
      }

      if (!container) {
        container = createDefaultContainer(apiUrl);
        const insertLocation = findInsertLocation();
        if (insertLocation) {
          if (insertLocation.before) {
            insertLocation.parent.insertBefore(container, insertLocation.before);
          } else {
            insertLocation.parent.insertBefore(container, insertLocation.parent.firstChild);
          }
        } else {
          console.warn('IntanDiscountCalculator: Could not find cart container, appending to body');
          document.body.appendChild(container);
        }
      }

      mountInto(container, apiUrl.replace(/\/$/, ''), config.liveSessionId);
    };

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', initialize);
    } else {
      initialize();
    }
  },
};

(window as any).IntanDiscountCalculator = api;

/**
 * Theme cart auto-init — only when THIS bundle's script tag carries
 * data-intan-api-url. Do not look at other scripts (e.g. intan-live-commerce):
 * LIVE loads this file dynamically and mounts via IntanDiscountCalculator.mount().
 * Scanning any script[data-intan-api-url] would inject a second cart into the
 * theme/body that survives after the live player closes.
 */
function autoInitFromOwnScript() {
  const own =
    (document.currentScript as HTMLScriptElement | null) ||
    document.querySelector<HTMLScriptElement>('script[src*="intan-discount-cart"]');
  const apiUrl = own?.dataset?.intanApiUrl;
  if (apiUrl) api.init({ apiUrl });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', autoInitFromOwnScript);
} else {
  autoInitFromOwnScript();
}
